import { type Item, type ItemMap } from "@outplayed/riot-assets";
import { ItemLink } from "../ItemLink";
import { isMythicItem } from "../item-parsers";
import { RecipeBranch, type ItemBranch } from "./RecipeBranch";
import { ItemCard } from "./ItemCard";
import { ItemDetails } from "./ItemDetails";
import { ItemBuildsInto } from "./ItemBuildsInto";
import { ItemCostAnalysis } from "./ItemCostAnalysis";
import { HeaderBullet } from "@ugg/shared/components/common/HeaderBullet";
import { useItemSEO } from "@ugg/shared/utils/seo-helpers";

interface ItemPageProps {
  itemId: number;
  itemDescription: JSX.Element | string;
  itemData: Item;
  items: ItemMap;
}

export function ItemPage(props: ItemPageProps) {
  const { itemId, itemDescription, itemData, items } = props;
  const getItemTitleAndDesc = useItemSEO({ ssr: true });

  const isMythic = isMythicItem(itemData?.description);

  const itemDataProps = {
    itemId: itemId,
    itemData,
    isMythic,
  };

  const getItemComponents = (itemData: Item): number[] => {
    const from = (itemData?.from || []).map((id) => Number(id));
    const components: number[] = itemData?.from ? from : itemData?.specialRecipe ? [itemData.specialRecipe] : [];
    return components;
  };

  const itemMap = (itemId: number): ItemBranch[] => {
    const components = getItemComponents(items[itemId]);
    return components.map((id) => ({ id, components: itemMap(id) }));
  };

  // TODO itemId:7024 sometimes bugs on the recipe tree when page refreshed
  const rootBranch: ItemBranch = { id: itemId, components: itemMap(itemId) };
  const itemsBuildsInto = itemData.into;

  return (
    <div className="items-home">
      {getItemTitleAndDesc(itemData.name)}
      <div className="items-home-header">
        <div className="items-home-header-pic">
          <ItemLink
            itemId={itemId}
            className={isMythic ? "items-pic-xlarge items-border-mythic-large" : "items-pic-xlarge items-border-normal-large"}
          />
        </div>
        <div className="items-header-text">
          <h1>{itemData?.name}</h1>
          {itemDescription}
        </div>
      </div>

      <div className="items-container">
        <div className="items-section items-recipe">
          <HeaderBullet>Recipe</HeaderBullet>
          <div className="recipe-container">
            <div>
              <ItemCard itemId={itemId} isMainItem isMythic={isMythic} />
              {rootBranch.components.map(({ id, components }, index) => (
                <RecipeBranch key={`${id}-${index}-1`} itemId={id} index={index} branchPath={[index]} rootBranch={rootBranch} />
              ))}
            </div>
          </div>
        </div>

        <ItemDetails {...itemDataProps} />

        {itemsBuildsInto && itemsBuildsInto.length > 0 && <ItemBuildsInto {...itemDataProps} />}

        <ItemCostAnalysis {...itemDataProps} />
      </div>
    </div>
  );
}
